<!-- 
Dialog for simulating a SAP reconciliation for testing purposes.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog sm">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>Reconcile with SAP (testing tool)</v-toolbar-title>
        </v-toolbar>
        <v-alert :value="true" type="info" class="ma-0">
          SAP integration is disabled in this environment. 
          To assist with testing you can simulate reconciling with SAP by completing the information below.
        </v-alert>
        <our-busy-indicator :show="loading"/>
        <v-card-text>
          <v-container grid-list-md pa-0>
            <v-layout wrap>
              <v-flex xs12>
                <v-select 
                  v-if="show"
                  :items="lookups.invoiceStatuses" :value="state.data.status" :rules="[rules.required]"
                  autofocus
                  label="Status*"
                  @input="update('status', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <v-flex xs12>
                <our-date-time-picker 
                  v-if="showClearingDate"
                  :value="state.data.clearingDate"
                  required
                  label="Clearing date*" @input="update('clearingDate', $event)"
                />
              </v-flex>
              <v-flex xs12>
                <our-currency-input 
                  v-if="showOutstandingBalance"
                  :value="state.data.outstandingBalance"
                  label="Outstanding balance" 
                  max="1000000000000"
                  @input="update('outstandingBalance', $event)"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="loading" flat @click="hide">Cancel</v-btn>
          <v-btn 
            :disabled="loading" 
            flat 
            color="primary" 
            type="submit" 
            @click.prevent="save"
          >
            Update invoice
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/invoices/action-types";
import * as mutation from "@/store/invoices/mutation-types";
import { getBlankReconciliationRecord } from "@/store/invoices/helpers";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";

export default {
  mixins: [filterMixins, lookupMixins, ruleMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoiceId: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      showValidationDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.invoices.reconcileTestingTool;
    },
    loading() {
      return this.state.loading;
    },
    showClearingDate() {
      return this.state.data.status == "Cleared";
    },
    showOutstandingBalance() {
      return this.state.data.status == "Open";
    }
  },
  watch: {
    show(nowShowing) {
      if (nowShowing) {
        this.$store.commit(mutation.RECONCILE_TESTING_TOOL_UPDATE, getBlankReconciliationRecord());
        this.$refs.form.reset();
      }
    }
  },
  methods: {
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.RECONCILE_TESTING_TOOL, this.invoiceId))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    update(field, value) {
      this.$store.commit(mutation.RECONCILE_TESTING_TOOL_UPDATE, { [field]: value });
      if (field == "status") {
        if (value != "Cleared") this.$store.commit(mutation.RECONCILE_TESTING_TOOL_UPDATE, { clearingDate: null });
        if (value != "Open") this.$store.commit(mutation.RECONCILE_TESTING_TOOL_UPDATE, { outstandingBalance: null });
      }
    },
    hide() {
      this.$emit("update:show", false);
    }
  }
};
</script>
